import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    question: 'What is DeploAI?',
    answer:
      'DeploAI is an AI-powered cloud and DevOps platform being built to help engineering teams deploy, operate, secure, monitor, and troubleshoot modern infrastructure from one place.',
  },
  {
    question: 'Who is DeploAI for?',
    answer:
      'DevOps and SRE teams, platform engineers, engineering leaders, and developers who work with cloud infrastructure and Kubernetes.',
  },
  {
    question: 'Does DeploAI replace Kubernetes or my cloud provider?',
    answer:
      'No. DeploAI is designed to operate alongside your existing cloud providers, Kubernetes clusters, and CI/CD systems, not replace them.',
  },
  {
    question: 'Which cloud providers are supported?',
    answer:
      'AWS, GCP, and Azure are the target ecosystem. Depth of support varies by capability — see the Platform Capabilities section for current status.',
  },
  {
    question: 'Can DeploAI deploy applications today?',
    answer:
      'Application and infrastructure deployment workflows are in active development. Talk to us to discuss current capabilities and timelines for your use case.',
  },
  {
    question: 'How does the AI assistant work?',
    answer:
      "The AI assistant is designed as an infrastructure-aware operations layer — helping detect, understand, and investigate issues, and recommend next steps grounded in your actual infrastructure state. The in-app assistant widget is a preview of this experience and isn't connected to a live backend yet.",
  },
  {
    question: 'Does DeploAI automatically change infrastructure?',
    answer:
      'Not today. Any automated action is being designed to run only within explicit permissions and controls that you grant — DeploAI does not make unattended infrastructure changes.',
  },
  {
    question: 'How does DeploAI handle permissions and security?',
    answer:
      'The platform is being designed around least-privilege access, auditability, and human-controlled automation. DeploAI does not currently hold SOC 2, ISO 27001, or other third-party compliance certifications.',
  },
  {
    question: 'Is DeploAI available today?',
    answer:
      "The DeploAI platform is under active development. Some capabilities are further along than others — get in touch to see current status and discuss early access.",
  },
];

const FAQ: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="py-32 relative overflow-hidden bg-dark-950">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-semibold mb-6">
            <span className="text-white">Frequently asked</span>{' '}
            <span className="gradient-text">questions</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="space-y-4"
        >
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={faq.question} className="glass rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                >
                  <span className="text-white font-medium">{faq.question}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 ml-4 transition-transform duration-200 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-6 pb-5 text-gray-400 leading-relaxed">{faq.answer}</div>
                )}
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
